import { notification } from "antd";
import secureLocalStorage from "react-secure-storage";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

export function showMessage(type, message, title) {
  if (type === "success") {
    notification.success({
      placement: "topRight",
      message: title ? title : "Success",
      description: message,
    });
  } else {
    notification.error({
      placement: "topRight",
      message: title ? title : "Error",
      description: message,
    });
  }
}
export function validateNumberInput(value) {
  return /^\d*\.?\d{0,2}$/.test(value);
}
export function notifyUser(
  type = "success",
  message,
  title = "Notification",
  placement = "topRight"
) {
  if (type === "error") {
    notification.error({
      message: title,
      description: message,
      placement: placement,
    });
  } else {
    notification.success({
      message: title,
      description: message,
      placement: placement,
    });
  }
}

export const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

export const getUserToken = () => secureLocalStorage.getItem("token");
export const getUserRefreshToken = () => secureLocalStorage.getItem("refreshToken");

export const getUserDetail = () =>
  JSON.parse(secureLocalStorage.getItem("user"));

export let currencyFormatter = (num) => {
  let split = "";
  if (num) {
    split = parseFloat(num).toFixed(2).toString().split(".");
    split[0] = split[0].toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    split = split.join(".");

    return split === 0 ? 0 : split;
  } else {
    return 0;
  }
};

export const logout = () => {
  secureLocalStorage.removeItem("token");
  secureLocalStorage.removeItem("user");
  secureLocalStorage.removeItem("expiresAt");
};

export let isFormComplete = (form) => {
  return Object.values(form).every((value) => {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === "number") {
      return true; // or you might want to check for specific number validation
    }
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return true; // for other types (boolean, object, etc.)
  });
};

export let isFormCompleteIgnore = (form, ignoreField) => {
  return Object.entries(form)
    .filter(([key]) => key !== ignoreField)
    .every(
      ([key, value]) => value !== "" && value !== null && value !== undefined
    );
};

export let isPercentageValid = (beneficiaries) => {
  const totalPercentage = beneficiaries.reduce(
    (sum, beneficiary) => sum + Number(beneficiary.percentageAllocation),
    0
  );
  return totalPercentage === 100;
};

export const logoutUser = () => {
  secureLocalStorage.removeItem("token");
  secureLocalStorage.removeItem("user");
  secureLocalStorage.removeItem("expiresAt");
  window.location.href = "/onboard/login";
};

export const exportToPDF = (
  columns,
  data,
  documentTitle,
  landscape = false
) => {
  const orientation = landscape ? "l" : "p"; // "l" for landscape, "p" for portrait
  const doc = new jsPDF({ orientation });
  const tableColumn = columns.map((col) => col.title);
  const tableRows = data.map((item) =>
    columns.map((col) => item[col.dataIndex])
  );

  doc.autoTable({
    head: [tableColumn],
    body: tableRows,
  });

  doc.save(`${documentTitle}.pdf`);
};

export const exportToCSV = (data, documentTitle) => {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, documentTitle);
  XLSX.writeFile(workbook, `${documentTitle}.csv`);
};

export const validateGhanaCard = (value) => {
  const GhanaCardPattern = /^GHA-\d{9}-\d$/;
  return GhanaCardPattern.test(value);
};

export function hasValidOldMutualDomain(email) {
  const validDomains = ["oldmutual.com", "oldmutual.com.gh"];
  const parts = email.trim().toLowerCase().split("@");

  return parts.length === 2 && validDomains.includes(parts[1]);
}

export function getLabelForXOptions(item) {
  if (item === "ONE") {
    return "1x";
  } else if (item === "TWO") {
    return "2x";
  } else if (item === "THREE") {
    return "3x";
  } else if (item === "FOUR") {
    return "4x";
  } else if (item === "FIVE") {
    return "5x";
  } else if (item === "SIX") {
    return "6x";
  } else if (item === "SEVEN") {
    return "7x";
  } else if (item === "EIGHT") {
    return "8x";
  } else if (item === "NINE") {
    return "9x";
  } else {
    return "10x";
  }
}

export function toDaysDate() {
  const currentDate = new Date();
  const formattedDate = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(currentDate);
  return formattedDate;
}

export const formatPolicyText = (input) => {
  return input
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const pdfBase64Export = async (pdfBlob) => {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(pdfBlob);
    reader.onloadend = () => {
      const base64 = reader.result.split(",")[1]; // remove data:*/*;base64,
      resolve(base64);
    };
    reader.onerror = reject;
  });
};

// ============================================
// GHANA PHONE NUMBER UTILITIES
// ============================================

/**
 * Valid Ghana mobile number prefixes (after 233)
 * Covers MTN, Vodafone, AirtelTigo, Glo
 */
export const GHANA_MOBILE_PREFIXES = [
  '20', '23', '24', '25', '26', '27', '28', '29', // MTN
  '50', '53', '54', '55', '56', '57', '58', '59', // Vodafone, AirtelTigo, Glo
];

/**
 * Normalize a phone number input to the 12-digit Ghana format (233XXXXXXXXX)
 * 
 * Rules:
 * - 05XXXXXXXX → 233XXXXXXXX (remove leading 0, add 233)
 * - 233537144161 → 233537144161 (keep as-is)
 * - +233537144161 → 233537144161 (remove +)
 * - 537144161 → 233537144161 (add 233)
 * 
 * @param {string} input - Raw phone number input
 * @returns {string} - Normalized 12-digit number starting with 233
 */
export function normalizeGhanaPhone(input) {
  if (!input) return '';
  
  // Remove all non-digit characters (+, -, spaces, etc.)
  let digits = input.toString().replace(/\D/g, '');
  
  // Rule 1: If starts with 0, remove it and add 233
  // This handles: 0572255447 (10 digits) → 233572255447
  // This handles: 05361177383 (11 digits) → 2335361177383
  if (digits.startsWith('0')) {
    digits = '233' + digits.substring(1);
  }
  // Rule 2: If already starts with 233, keep as-is
  // This handles: 233517144161 → 233517144161
  else if (digits.startsWith('233')) {
    // Already correct format, do nothing
  }
  // Rule 3: If no prefix and 9 digits, add 233
  // This handles: 572255447 → 233572255447
  else if (digits.length === 9) {
    digits = '233' + digits;
  }
  // Rule 4: Any other case without 233 prefix, add 233
  else if (!digits.startsWith('233')) {
    digits = '233' + digits;
  }
  
  // Ghana phone numbers are 12 digits (233 + 9 digits)
  // If result is longer than 12, it likely had extra digits in input
  // Keep only valid 12-digit format
  if (digits.length > 12) {
    // If starts with 233, take only first 12 digits
    if (digits.startsWith('233')) {
      digits = digits.substring(0, 12);
    }
  }
  
  return digits;
}

/**
 * Validate if a phone number is a valid Ghana mobile number
 * 
 * @param {string} input - Phone number (normalized or raw)
 * @returns {object} - Validation result with details
 */
export function validateGhanaPhone(input) {
  const normalized = normalizeGhanaPhone(input);
  
  if (!normalized) {
    return {
      isValid: false,
      error: 'Phone number is required',
      normalized: '',
    };
  }
  
  // Must start with 233
  if (!normalized.startsWith('233')) {
    return {
      isValid: false,
      error: 'Phone number must start with 233',
      normalized,
    };
  }
  
  // Must be exactly 12 digits
  if (normalized.length !== 12) {
    return {
      isValid: false,
      error: `Phone number must be exactly 12 digits (e.g., 233537144161). Current length: ${normalized.length}`,
      normalized,
    };
  }
  
  // Check for valid mobile prefix (the digit after 233 should not be 0)
  const prefixAfter233 = normalized.substring(3, 5);
  if (prefixAfter233.startsWith('0')) {
    return {
      isValid: false,
      error: 'Invalid phone number format. After 233, do not use 0. Example: 233537144161',
      normalized,
    };
  }
  
  // Check against known valid Ghana mobile prefixes
  const isValidPrefix = GHANA_MOBILE_PREFIXES.includes(prefixAfter233);
  if (!isValidPrefix) {
    return {
      isValid: false,
      error: 'Please enter a valid Ghana mobile number',
      normalized,
    };
  }
  
  return {
    isValid: true,
    error: null,
    normalized,
  };
}

/**
 * Quick validation - returns true/false only
 * @param {string} input - Phone number to validate
 * @returns {boolean}
 */
export function isValidGhanaPhone(input) {
  return validateGhanaPhone(input).isValid;
}

/**
 * Format phone number for display
 * 233537144161 → +233 53 714 4161
 * 
 * @param {string} input - Phone number
 * @returns {string} - Formatted phone number
 */
export function formatGhanaPhoneForDisplay(input) {
  const normalized = normalizeGhanaPhone(input);
  if (normalized.length !== 12) return normalized;
  
  // Format: +233 XX XXX XXXX
  return `+${normalized.substring(0, 3)} ${normalized.substring(3, 5)} ${normalized.substring(5, 8)} ${normalized.substring(8)}`;
}

/**
 * Get input max length based on current value
 * - If starts with 233: 12 digits max
 * - If starts with 0: 10 digits max (local format)
 * - Otherwise: 12 digits max
 * 
 * @param {string} value - Current input value
 * @returns {number}
 */
export function getPhoneInputMaxLength(value) {
  const digits = (value || '').toString().replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length <= 10) return 10;
  return 12;
}

/**
 * Check if input looks like a phone number (contains mostly digits)
 * @param {string} input - User input
 * @returns {boolean}
 */
export function looksLikePhoneNumber(input) {
  if (!input) return false;
  const trimmed = input.trim();
  // Contains mostly digits, spaces, hyphens, plus signs
  const digitCount = (trimmed.match(/\d/g) || []).length;
  const nonDigitCount = trimmed.length - digitCount;
  return digitCount >= 9 && nonDigitCount <= 3; // Allow +, -, spaces
}

/**
 * Process phone input for onChange handlers
 * - Auto-normalizes as user types
 * - Returns object with normalized value and validation state
 * 
 * @param {string} input - Raw input from user
 * @param {object} options - Options { validateOnType: boolean }
 * @returns {object} - { value, isValid, error, displayValue }
 */
export function processPhoneInput(input, options = {}) {
  const { validateOnType = false } = options;
  const normalized = normalizeGhanaPhone(input);
  
  // For display, we show the normalized version
  const displayValue = normalized;
  
  if (!validateOnType) {
    return {
      value: normalized,
      displayValue,
      isValid: null, // Not validated yet
      error: null,
    };
  }
  
  // Only validate if we have enough digits
  if (normalized.length === 12) {
    const validation = validateGhanaPhone(normalized);
    return {
      value: normalized,
      displayValue,
      isValid: validation.isValid,
      error: validation.error,
    };
  }
  
  return {
    value: normalized,
    displayValue,
    isValid: null,
    error: null,
  };
}
